interface ConvertedImage {
    originalName: string;
    webpBlob: Blob;
    originalSize: number;
    convertedSize: number;
}

// Helper: Check for WebP support.
function supportsWebP(): boolean {
    const canvas = document.createElement('canvas');
    return canvas.toDataURL('image/webp').startsWith('data:image/webp');
}

// Helper: Load an image from a File object.
function loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const imageUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onerror = (err) => {
            console.error(`Error loading image ${file.name}`, err);
            URL.revokeObjectURL(imageUrl);
            reject(err);
        };
        img.onload = () => {
            console.log(`Image loaded: ${img.width}x${img.height}`);
            URL.revokeObjectURL(imageUrl);
            resolve(img);
        };
        img.src = imageUrl;
    });
}

// Helper: Create a canvas scaled from the image.
function createScaledCanvas(img: HTMLImageElement, scale: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');
    // Draw the image with scaling.
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas;
}

// Helper: Wrap canvas.toBlob in a Promise.
function convertCanvasToBlob(
    canvas: HTMLCanvasElement,
    format: string,
    quality: number
): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error(`Blob creation failed at quality: ${quality}`));
            }
        }, format, quality);
    });
}

// Helper: Try to convert the canvas with quality reduction until the blob is below the threshold.
// If quality falls below the minimum, the function throws.
async function tryQualityConversion(
    canvas: HTMLCanvasElement,
    format: string,
    maxSizeBytes: number,
    quality: number,
    minQuality: number
): Promise<Blob> {
    console.log(`Trying conversion at quality: ${quality}`);
    const blob = await convertCanvasToBlob(canvas, format, quality);
    if (blob.size <= maxSizeBytes) {
        return blob;
    }
    console.warn(`Blob size (${blob.size} bytes) exceeds max size (${maxSizeBytes} bytes) at quality ${quality}.`);
    const nextQuality = quality - 0.1;
    if (nextQuality >= minQuality) {
        console.warn(`Reducing quality to ${nextQuality} and retrying.`);
        return tryQualityConversion(canvas, format, maxSizeBytes, nextQuality, minQuality);
    } else {
        // Throwing error so that resolution can be reduced.
        throw new Error('Minimum quality reached');
    }
}

// Helper: Try conversion with quality first. If quality alone is not enough,
// reduce resolution (scale) and retry. The function recurses until a blob is found
// or the scale reaches the minimum allowed.
async function tryConversionWithScaling(
    img: HTMLImageElement,
    format: string,
    maxSizeBytes: number,
    scale: number,
    quality: number,
    minQuality: number,
    minScale: number
): Promise<Blob> {
    console.log(`Attempting conversion with scale: ${scale}`);
    const canvas = createScaledCanvas(img, scale);
    try {
        return await tryQualityConversion(canvas, format, maxSizeBytes, quality, minQuality);
    } catch (error) {
        // If we've reached the lowest acceptable resolution, stop.
        if (scale <= minScale) {
            throw new Error(`Could not compress image below ${maxSizeBytes / (1024 * 1024)}MB even at minimum resolution.`);
        }
        // Reduce the scale (reduce resolution by 10%) and retry.
        const newScale = scale * 0.9;
        console.warn(`Quality conversion failed at scale ${scale}. Reducing resolution to ${Math.round(newScale * 100)}% and retrying.`);
        return tryConversionWithScaling(img, format, maxSizeBytes, newScale, quality, minQuality, minScale);
    }
}

// Main function: Convert image file to WebP (or JPEG fallback) under a maximum size,
// using both quality and resolution reduction if needed.
export async function convertToWebP(file: File, maxSizeMb = 1): Promise<ConvertedImage> {
    const format = supportsWebP() ? 'image/webp' : 'image/jpeg';
    if (format === 'image/jpeg')
        console.warn('WebP not supported; falling back to JPEG.');
    
    const maxSizeBytes = maxSizeMb * 1024 * 1024;
    console.log(`Starting conversion for file: ${file.name}`);

    // Load the image.
    const img = await loadImage(file);

    // Define parameters.
    const initialQuality = 0.9;
    const minQuality = 0.4;
    const initialScale = 1; // Start with the original resolution.
    const minScale = 0.1; // Do not reduce below 10% of original resolution.

    // Attempt conversion with both quality and resolution reduction.
    const blob = await tryConversionWithScaling(img, format, maxSizeBytes, initialScale, initialQuality, minQuality, minScale);

    // Log final results.
    const originalSizeKB = file.size / 1000;
    const compressedSizeKB = blob.size / 1000;
    const reductionPct = ((originalSizeKB - compressedSizeKB) / originalSizeKB) * 100;
    console.log(`Compressed ${file.name} from ${originalSizeKB.toFixed(2)}KB to ${compressedSizeKB.toFixed(2)}KB (${reductionPct.toFixed(2)}%).`);

    return {
        originalName: file.name.replace(/\.[^/.]+$/, ''),
        webpBlob: blob,
        originalSize: file.size,
        convertedSize: blob.size,
    };
}
